/**
 * "Show the model's working" — a per-user preference for whether raw model reasoning is
 * requested and displayed alongside a Document Intelligence run.
 *
 * DEFAULT OFF. Off means the client does not ask for `reasoning` events at all, so none
 * are produced and none can be displayed. It is not a display filter over data that was
 * fetched anyway.
 *
 * WHY `localStorage` AND NOT `sessionStorage`. `sessionStorage` is scoped to one tab.
 * A preference stored there silently reverts in every other tab and in every new tab,
 * which reads to the user as the setting not sticking. This repository already has one
 * instance of exactly that mistake. The preference is meant to persist across tabs and
 * across sessions, so it lives in `localStorage`.
 *
 * PER USER. The key is suffixed with the user id, so two people sharing a workstation
 * profile do not inherit each other's setting — in particular, one person turning raw
 * reasoning on does not turn it on for the next person to sign in on that machine.
 */

const STORAGE_PREFIX = "kontractai_docint_show_working";

/** Storage key for a given user. Anonymous/unknown users get their own bucket. */
export function showWorkingStorageKey(userId?: string | null): string {
  const id = String(userId || "").trim();
  return id ? `${STORAGE_PREFIX}:${id}` : `${STORAGE_PREFIX}:anonymous`;
}

/** Reads the stored preference. Anything other than a stored "on" is off. */
export function readShowWorking(userId?: string | null): boolean {
  try {
    return localStorage.getItem(showWorkingStorageKey(userId)) === "on";
  } catch {
    // Storage unavailable (private mode, disabled cookies). Falling back to OFF keeps
    // the safe default: no reasoning requested, nothing extra on screen.
    return false;
  }
}

export function writeShowWorking(userId: string | null | undefined, value: boolean): void {
  try {
    localStorage.setItem(showWorkingStorageKey(userId), value ? "on" : "off");
  } catch { /* storage unavailable — the preference simply does not persist */ }
}
